// src/components/ChatBubble.js — Floating chat bubble (bottom-left) with quick replies + sound
import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { useAuth } from "../AuthContext";
import { ADMIN_UID, CHAT_QUICK_REPLIES, sanitize, formatTime, playNotificationSound, generateBotReply } from "../config";
import {
  collection, addDoc, query, where, orderBy, onSnapshot, Timestamp, doc, updateDoc,
} from "firebase/firestore";

export default function ChatBubble({ orders }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadMap, setUnreadMap] = useState({});
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const bottomRef = useRef(null);
  const prevMsgCount = useRef(0);
  const isAdmin = user?.uid === ADMIN_UID;

  // Listen to messages for all user's orders to count unread
  useEffect(() => {
    if (!orders || orders.length === 0) return;
    const orderIds = orders.map((o) => o.id);
    const chunks = [];
    for (let i = 0; i < orderIds.length; i += 30) chunks.push(orderIds.slice(i, i + 30));
    const unsubs = chunks.map((chunk) => {
      const q = query(collection(db, "messages"), where("orderId", "in", chunk), orderBy("createdAt", "desc"));
      return onSnapshot(q, (snap) => {
        const counts = {};
        snap.docs.forEach((d) => {
          const msg = d.data();
          if (msg.senderId !== user.uid) counts[msg.orderId] = (counts[msg.orderId] || 0) + 1;
        });
        setUnreadMap((prev) => ({ ...prev, ...counts }));
      });
    });
    return () => unsubs.forEach((u) => u());
  }, [orders, user]);

  // Listen to messages for selected order
  useEffect(() => {
    if (!selectedOrder) return;
    const q = query(collection(db, "messages"), where("orderId", "==", selectedOrder), orderBy("createdAt", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sound notification on new messages from others
      if (msgs.length > prevMsgCount.current && prevMsgCount.current > 0) {
        const latest = msgs[msgs.length - 1];
        if (latest && latest.senderId !== user.uid) playNotificationSound("message");
      }
      prevMsgCount.current = msgs.length;
      setMessages(msgs);
    });
    return unsub;
  }, [selectedOrder, user]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const totalUnread = Object.values(unreadMap).reduce((a, b) => a + b, 0);

  const sendMessage = async (e, overrideText) => {
    if (e) e.preventDefault();
    const clean = sanitize(overrideText || text, 1000).trim();
    if (!clean || sending) return;
    setSending(true);
    try {
      await addDoc(collection(db, "messages"), {
        orderId: selectedOrder,
        senderId: user.uid,
        senderName: isAdmin ? "PL Support" : (user.displayName || "Customer"),
        isAdmin, text: clean, createdAt: Timestamp.now(),
      });
      setText("");
      setShowQuickReplies(false);

      // Auto-reply bot for customer messages (not admin)
      if (!isAdmin) {
        const order = orders?.find(o => o.id === selectedOrder);
        const botReply = generateBotReply(clean, order);
        if (botReply) {
          setTimeout(async () => {
            try {
              await addDoc(collection(db, "messages"), {
                orderId: selectedOrder,
                senderId: "bot",
                senderName: "PL Bot 🤖",
                isAdmin: false,
                isBot: true,
                text: botReply,
                createdAt: Timestamp.now(),
              });
            } catch (err) { console.error("Bot reply failed:", err); }
          }, 800);
        }
      }
    } catch (err) { console.error("Send failed:", err); }
    setSending(false);
  };

  const requestAgent = async () => {
    if (!selectedOrder) return;
    try {
      await updateDoc(doc(db, "orders", selectedOrder), { priority: "urgent" });
      await addDoc(collection(db, "messages"), {
        orderId: selectedOrder,
        senderId: "system",
        senderName: "System",
        isAdmin: false,
        isSystem: true,
        text: "🚨 Customer has requested a live agent. This chat is now marked as URGENT.",
        createdAt: Timestamp.now(),
      });
      playNotificationSound("message");
    } catch (err) { console.error("Agent request failed:", err); }
  };

  const activeOrders = orders?.filter((o) => !["cancelled", "voided"].includes(o.status)) || [];

  return (
    <>
      {/* Floating button */}
      <button
        className={`chat-fab ${open ? "chat-fab-active" : ""} ${totalUnread > 0 && !open ? "chat-fab-pulse" : ""}`}
        onClick={() => setOpen(!open)} title="Chat with support"
      >
        {open ? (
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        ) : (
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
        )}
        {totalUnread > 0 && !open && <span className="chat-fab-badge">{totalUnread > 9 ? "9+" : totalUnread}</span>}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="chat-bubble-panel animate-slide-up">
          {!selectedOrder ? (
            <>
              <div className="chat-bubble-header">
                <h3 style={{ fontSize: 16, fontWeight: 700 }}>{isAdmin ? "Customer Chats" : "Chat with Support"}</h3>
                <button onClick={() => setOpen(false)} className="chat-close">✕</button>
              </div>
              <div className="chat-bubble-list">
                {activeOrders.length === 0 ? (
                  <div style={{ padding: 32, textAlign: "center", color: "#64748b", fontSize: 13 }}>No active orders to chat about.</div>
                ) : (
                  activeOrders.map((order) => (
                    <button key={order.id} className="chat-order-item" onClick={() => { setSelectedOrder(order.id); prevMsgCount.current = 0; }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontWeight: 600, color: "#e2e8f0", fontSize: 13 }}>#{order.id.slice(0, 8)}</span>
                          <span className={`badge badge-${order.status}`} style={{ fontSize: 10, padding: "2px 8px" }}>{order.status}</span>
                          {order.priority === "urgent" && <span style={{ color: "#ef4444", fontSize: 11 }}>URGENT</span>}
                        </div>
                        <p style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                          {isAdmin ? `${order.userName} - ` : ""}{order.senderCity} -> {order.recipientCity}
                        </p>
                      </div>
                      {unreadMap[order.id] > 0 && <span className="chat-unread-dot">{unreadMap[order.id]}</span>}
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#475569" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              <div className="chat-bubble-header">
                <button onClick={() => { setSelectedOrder(null); setMessages([]); setShowQuickReplies(false); }}
                  style={{ background: "none", border: "none", color: "#d4a053", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600 }}>
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>Back
                </button>
                <span style={{ fontSize: 12, color: "#64748b", fontFamily: "monospace" }}>#{selectedOrder.slice(0, 8)}</span>
                <button onClick={() => setOpen(false)} className="chat-close">✕</button>
              </div>
              <div className="chat-bubble-messages">
                {messages.length === 0 ? (
                  <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 13 }}>No messages yet. Start the conversation!</div>
                ) : (
                  messages.map((msg) => {
                    const isMine = msg.senderId === user.uid;
                    const isBot = msg.isBot || msg.senderId === "bot";
                    const isSys = msg.isSystem || msg.senderId === "system";
                    if (isSys) {
                      return (
                        <div key={msg.id} style={{ textAlign: "center", padding: "8px 16px", margin: "4px 0" }}>
                          <span style={{ fontSize: 11, color: "#ef4444", background: "rgba(239,68,68,0.08)",
                            padding: "4px 12px", borderRadius: 12, fontWeight: 600 }}>{msg.text}</span>
                        </div>
                      );
                    }
                    return (
                      <div key={msg.id} className={`chat-bubble ${isMine ? "chat-mine" : "chat-theirs"}`}
                        style={isBot ? { background: "rgba(99,102,241,0.1)", borderColor: "rgba(99,102,241,0.2)" } : undefined}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 2 }}>
                          <span className="chat-sender" style={isBot ? { color: "#818cf8" } : undefined}>{msg.senderName}</span>
                          <span className="chat-time">{formatTime(msg.createdAt)}</span>
                        </div>
                        <p className="chat-text">{msg.text}</p>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              {/* Quick replies (admin only) */}
              {isAdmin && showQuickReplies && (
                <div className="quick-replies animate-slide-down">
                  {CHAT_QUICK_REPLIES.map((qr) => (
                    <button key={qr.label} className="quick-reply-btn" onClick={() => sendMessage(null, qr.text)}>
                      {qr.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Agent request button (customer only) */}
              {!isAdmin && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px",
                  borderTop: "1px solid rgba(226,232,240,0.08)", background: "rgba(15,23,42,0.4)" }}>
                  {(() => {
                    const currentOrder = orders?.find(o => o.id === selectedOrder);
                    const isUrgent = currentOrder?.priority === "urgent";
                    return (
                      <button type="button" onClick={requestAgent} disabled={isUrgent}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px",
                          fontSize: 11, fontWeight: 700, borderRadius: 8, border: "none", cursor: isUrgent ? "default" : "pointer",
                          background: isUrgent ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.1)",
                          color: isUrgent ? "#f87171" : "#ef4444", opacity: isUrgent ? 0.7 : 1,
                          transition: "all 0.2s" }}>
                        <span style={{ fontSize: 14 }}>🚨</span>
                        {isUrgent ? "Agent Requested" : "Agent"}
                      </button>
                    );
                  })()}
                  <span style={{ fontSize: 10, color: "#475569", flex: 1 }}>
                    {orders?.find(o => o.id === selectedOrder)?.priority === "urgent"
                      ? "A support agent has been notified"
                      : "Need a real person? Click Agent"}
                  </span>
                </div>
              )}

              <form onSubmit={sendMessage} className="chat-input-bar">
                {isAdmin && (
                  <button type="button" className="chat-quick-toggle"
                    onClick={() => setShowQuickReplies(!showQuickReplies)}
                    title="Quick replies">
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                      <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                    </svg>
                  </button>
                )}
                <input className="chat-input" value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message..." maxLength={1000} disabled={sending} />
                <button type="submit" className="chat-send" disabled={sending || !text.trim()}>
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
